"use client";

import React from "react";

type Accent = "teal" | "purple" | "green" | "amber";

type DarkStatCardProps = {
  label: string;
  value: React.ReactNode;
  badge?: string;
  accent?: Accent;
  sub?: string;
  sparkData?: number[];
};

// Color is used as a quiet *signal* (identity dot, badge, sparkline) — the
// headline number stays slate-900 for authority + maximum legibility.
const ACCENT: Record<Accent, { dot: string; badge: string }> = {
  teal:   { dot: "#0d9488", badge: "bg-teal-50 dark:bg-teal-950/40 text-teal-700 dark:text-teal-300" },
  purple: { dot: "#7c3aed", badge: "bg-violet-50 dark:bg-violet-950/40 text-violet-700 dark:text-violet-300" },
  green:  { dot: "#16a34a", badge: "bg-green-50 dark:bg-green-950/40 text-green-700 dark:text-green-300" },
  amber:  { dot: "#d97706", badge: "bg-amber-50 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300" },
};

function DarkSparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length < 2) return null;
  const w = 96, h = 34;
  const min = Math.min(...data), max = Math.max(...data), range = max - min || 1;
  const pts = data.map((v, i) => [
    (i / (data.length - 1)) * w,
    h - ((v - min) / range) * (h - 6) - 3,
  ]);
  const path = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const uid = `spark-${color.replace(/[^a-z0-9]/gi, "")}`;
  const last = pts[pts.length - 1];

  return (
    <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} aria-hidden="true" style={{ overflow: "visible", display: "block", flexShrink: 0 }}>
      <defs>
        <linearGradient id={uid} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.28" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={`${path} L${w},${h} L0,${h} Z`} fill={`url(#${uid})`} />
      <path d={path} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx={last[0]} cy={last[1]} r="2.5" fill={color} />
    </svg>
  );
}

export default function DarkStatCard({ label, value, badge, accent = "teal", sub, sparkData }: DarkStatCardProps) {
  const a = ACCENT[accent];
  return (
    <div className="group rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 dark:hover:border-slate-700 hover:shadow-md">
      <div className="mb-2 flex items-start justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: a.dot }} aria-hidden />
          <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{label}</span>
        </div>
        {badge && (
          <span className={`rounded px-1.5 py-0.5 text-xs font-bold uppercase tracking-wide ${a.badge}`}>
            {badge}
          </span>
        )}
      </div>

      <div
        className="my-2 break-all text-[32px] font-bold leading-none tracking-tight text-slate-900 dark:text-slate-100 tabular-nums"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        {value}
      </div>

      <div className="mt-3 flex items-end justify-between">
        <span className="text-xs text-slate-600 dark:text-slate-400">{sub ?? "Year to date"}</span>
        {sparkData && <DarkSparkline data={sparkData} color={a.dot} />}
      </div>
    </div>
  );
}
